import Phaser from 'phaser';
import { PixelCanvas } from './pixel/PixelCanvas';
import type { EnemyRuntime } from '@/domain/combat/types';
import { buildDirectionalCreatureRenderer } from './DirectionalCreatureRenderer';
import { buildWildEnemyRenderer } from './WildEnemyRenderer';

const DISPLAY_SCALE = 3;
const LOGICAL_SCALE = 2;
const CANVAS = 32;

export function faunaVisualMetrics(): {
  canvas: number;
  logicalScale: number;
  displayScale: number;
  screenPixelSize: number;
} {
  return {
    canvas: CANVAS,
    logicalScale: LOGICAL_SCALE,
    displayScale: DISPLAY_SCALE,
    screenPixelSize: LOGICAL_SCALE * DISPLAY_SCALE,
  };
}

export interface EnemyRendererHandle {
  container: Phaser.GameObjects.Container;
  rid: number;
  setPos(x: number, y: number): void;
  setHp(hp: number, max: number): void;
  setState(enemy: EnemyRuntime): void;
  flashHit(): void;
  destroy(): void;
}

export function buildEnemyRenderer(scene: Phaser.Scene, e: EnemyRuntime, reducedMotion = false): EnemyRendererHandle {
  if (e.kind === 'wild') {
    return buildWildEnemyRenderer(scene, e, reducedMotion, buildDirectionalCreatureRenderer);
  }
  const container = scene.add.container(e.x, e.y);
  container.setScale(DISPLAY_SCALE);
  container.setDepth(7);

  const shadow = scene.add.graphics();
  const shadowPx = new PixelCanvas(shadow);
  shadowPx.ellipseFill(0, 4, 5, 1, 0x000000);
  shadow.setScale(LOGICAL_SCALE);

  const body = scene.add.graphics();
  const bodyPx = new PixelCanvas(body);
  drawByDefId(bodyPx, e.defId);
  body.setScale(LOGICAL_SCALE);

  // HP bar (não escala com pixel)
  const hpBg = scene.add.rectangle(0, -12, 20, 2, 0x000000, 0.7).setOrigin(0.5);
  const hpFg = scene.add.rectangle(0, -12, 20, 2, 0xff5566).setOrigin(0.5);
  const stateCue = scene.add.text(0, -17, '', {
    fontFamily: 'monospace', fontSize: '5px', color: '#ffe08a', stroke: '#241515', strokeThickness: 1,
  }).setOrigin(0.5);

  container.add([shadow, body, hpBg, hpFg, stateCue]);

  function setPos(x: number, y: number): void {
    container.setPosition(Math.round(x), Math.round(y));
  }
  function setHp(hp: number, max: number): void {
    const pct = Math.max(0, Math.min(1, hp / Math.max(1, max)));
    hpFg.scaleX = pct;
    hpFg.setVisible(hp < max);
    hpBg.setVisible(hp < max);
  }
  function flashHit(): void {
    scene.tweens.add({
      targets: body,
      alpha: 0.3,
      duration: 80,
      yoyo: true,
    });
  }
  function setState(enemy: EnemyRuntime): void {
    container.setAlpha(enemy.behavior.kind === 'recover' ? 0.78 : 1);
    const cues = [
      enemy.behavior.kind === 'telegraph' ? '!' : '',
      enemy.rank === 'dominant' ? '◆' : '',
      enemy.burnUntil ? '*' : '',
      enemy.iceUntil ? '~' : '',
    ].filter(Boolean);
    stateCue.setText(cues.join(''));
  }
  function destroy(): void {
    scene.tweens.killTweensOf(body);
    container.destroy();
  }

  setState(e);
  return { container, rid: e.rid, setPos, setHp, setState, flashHit, destroy };
}

// ========== sprites pixel art por defId ==========

function drawByDefId(px: PixelCanvas, id: string): void {
  switch (id) {
    case 'meadow-bunbun': drawBunbun(px); break;
    case 'meadow-bee': drawBee(px); break;
    case 'meadow-bigboar': drawBigboar(px); break;
    case 'forest-wolfpup': drawWolfpup(px); break;
    case 'forest-spider': drawSpider(px); break;
    case 'forest-darkspit': drawDarkspit(px); break;
    case 'cave-bat': drawBat(px); break;
    case 'cave-rockling': drawRockling(px); break;
    case 'cave-crystalfly': drawCrystalfly(px); break;
    case 'coast-crabling': drawCrabling(px); break;
    case 'coast-jelly': drawJelly(px); break;
    case 'coast-shellbeast': drawShellbeast(px); break;
    default: drawDefault(px); break;
  }
}

function drawDefault(px: PixelCanvas): void {
  px.ellipseStroke(0, 0, 5, 5, 0x4a245f);
  for (let y = -4; y <= 4; y++) {
    const w = Math.round(4 * Math.sqrt(Math.max(0, 1 - (y * y) / 16)));
    if (w > 0) px.hLine(-w, y, w * 2 + 1, 0xa14ad0);
  }
  px.hLine(-2, -3, 3, 0xc982ee);
  px.px(-1, -1, 0x1e1028); px.px(2, -1, 0x1e1028);
  px.hLine(0, 2, 2, 0x6d348c);
}

function drawBunbun(px: PixelCanvas): void {
  // coelho marrom (orelhas longas)
  px.rect(-3, -2, 7, 5, 0xc9a472);
  px.rect(-2, -3, 5, 1, 0xc9a472);
  // outline
  px.rect(-3, -2, 1, 5, 0x6a4a2a);
  px.rect(3, -2, 1, 5, 0x6a4a2a);
  px.rect(-2, 3, 5, 1, 0x6a4a2a);
  // orelhas
  px.vLine(-1, -6, 4, 0xc9a472);
  px.vLine(1, -6, 4, 0xc9a472);
  px.px(-1, -6, 0xff8db1);
  px.px(1, -6, 0xff8db1);
  // olhos
  px.px(-1, -1, 0x000000);
  px.px(2, -1, 0x000000);
  // nariz
  px.px(0, 1, 0x6a3a2a);
}

function drawBee(px: PixelCanvas): void {
  px.hLine(-3, -3, 7, 0x5b3a16);
  px.hLine(-3, 3, 7, 0x5b3a16);
  // amarelo+preto listrado
  for (let y = -2; y <= 2; y++) {
    px.hLine(-3, y, 7, y % 2 === 0 ? 0xffd84a : 0x000000);
  }
  // asa
  px.rect(-4, -3, 3, 2, 0xffffff);
  px.px(-4, -3, 0xddddee);
  px.rect(2, -3, 3, 2, 0xffffff);
  px.px(4, -3, 0xddddee);
  // olhos
  px.px(-2, -1, 0xff0000);
  px.px(2, -1, 0xff0000);
  px.px(0, 2, 0xffef8a);
}

function drawBigboar(px: PixelCanvas): void {
  // javali grande marrom
  for (let y = -3; y <= 4; y++) {
    const w = y < 3 ? 5 : 4;
    px.hLine(-w, y, w * 2 + 1, 0x6a4423);
  }
  // sombra
  px.hLine(-5, -3, 11, 0x4a2a18);
  // presas
  px.px(-4, 3, 0xffffff);
  px.px(4, 3, 0xffffff);
  // olhos vermelhos
  px.px(-2, -1, 0xff2222);
  px.px(2, -1, 0xff2222);
  // crina
  px.hLine(-3, -4, 7, 0x3a1a08);
}

function drawWolfpup(px: PixelCanvas): void {
  px.rect(-3, -2, 7, 5, 0x8a6a4a);
  px.rect(-3, -4, 2, 2, 0x8a6a4a);
  px.rect(2, -4, 2, 2, 0x8a6a4a);
  // outline
  px.rect(-3, -2, 1, 5, 0x4a3018);
  px.rect(3, -2, 1, 5, 0x4a3018);
  // olhos amarelos
  px.px(-1, -1, 0xffd84a);
  px.px(2, -1, 0xffd84a);
  // dente
  px.px(0, 2, 0xffffff);
  px.px(1, 2, 0xffffff);
}

function drawSpider(px: PixelCanvas): void {
  // corpo
  for (let y = -2; y <= 2; y++) {
    const w = Math.round(3 * Math.sqrt(Math.max(0, 1 - (y * y) / 4)));
    if (w > 0) px.hLine(-w, y, w * 2 + 1, 0x2a1a3a);
  }
  // 4 patas
  px.hLine(-5, -1, 3, 0x2a1a3a);
  px.hLine(3, -1, 3, 0x2a1a3a);
  px.hLine(-5, 1, 3, 0x2a1a3a);
  px.hLine(3, 1, 3, 0x2a1a3a);
  px.hLine(-6, -3, 4, 0x2a1a3a);
  px.hLine(3, -3, 4, 0x2a1a3a);
  px.hLine(-6, 3, 4, 0x2a1a3a);
  px.hLine(3, 3, 4, 0x2a1a3a);
  px.hLine(-2, -2, 4, 0x5b3b70);
  // olhos vermelhos
  px.px(-1, -1, 0xff0000);
  px.px(1, -1, 0xff0000);
}

function drawDarkspit(px: PixelCanvas): void {
  // tank sombra
  for (let y = -3; y <= 4; y++) {
    const w = y < 0 ? 4 : 5;
    px.hLine(-w, y, w * 2 + 1, 0x3a2a4a);
  }
  // chifres
  px.px(-3, -4, 0x6a5a8a);
  px.px(3, -4, 0x6a5a8a);
  px.px(-3, -5, 0x6a5a8a);
  px.px(3, -5, 0x6a5a8a);
  // olhos brancos cintilantes
  px.px(-1, -1, 0xffffff);
  px.px(2, -1, 0xffffff);
}

function drawBat(px: PixelCanvas): void {
  // corpo pequeno + asas grandes
  px.rect(-1, -1, 3, 3, 0x2a2a3a);
  // asas
  px.rect(-5, -2, 4, 1, 0x2a2a3a);
  px.rect(2, -2, 4, 1, 0x2a2a3a);
  px.px(-5, -1, 0x2a2a3a);
  px.px(5, -1, 0x2a2a3a);
  px.px(-1, -3, 0x2a2a3a);
  px.px(1, -3, 0x2a2a3a);
  px.hLine(-4, -1, 3, 0x504360);
  px.hLine(2, -1, 3, 0x504360);
  // olhos
  px.px(-1, -1, 0xff5555);
  px.px(1, -1, 0xff5555);
  px.px(0, 2, 0xffffff);
}

function drawRockling(px: PixelCanvas): void {
  // pedra com olhinhos
  for (let y = -3; y <= 4; y++) {
    const w = 5 - Math.abs(y) > 0 ? Math.min(5, 5 - Math.abs(y) + 3) : 0;
    px.hLine(-w, y, w * 2 + 1, 0x6a667a);
  }
  // olhos
  px.px(-2, -1, 0xffffff);
  px.px(2, -1, 0xffffff);
  px.px(-2, -1, 0x000000);
  px.px(2, -1, 0x000000);
  // crack
  px.hLine(-3, 1, 6, 0x3a3848);
  px.hLine(-4, -3, 4, 0xa4a0b2);
  px.px(4, 2, 0x504d5c);
}

function drawCrystalfly(px: PixelCanvas): void {
  // corpo cristal turquesa + asas brancas
  px.rect(-1, -2, 3, 5, 0x4ad4ff);
  px.px(0, -3, 0xb0eaff);
  // asas
  px.rect(-4, -2, 3, 2, 0xffffff);
  px.rect(2, -2, 3, 2, 0xffffff);
  px.px(-4, -1, 0xb0eaff);
  px.px(4, -1, 0xb0eaff);
  px.px(-1, -1, 0x14394b);
  px.px(1, -1, 0x14394b);
  px.px(0, 1, 0xffffff);
}

function drawCrabling(px: PixelCanvas): void {
  // caranguejo vermelho
  px.rect(-3, -1, 7, 3, 0xc9462a);
  // pernas
  px.hLine(-5, 1, 2, 0x6a2010);
  px.hLine(3, 1, 2, 0x6a2010);
  // garras
  px.rect(-5, -2, 2, 2, 0xc9462a);
  px.rect(3, -2, 2, 2, 0xc9462a);
  // olhos
  px.px(-1, -2, 0x000000);
  px.px(1, -2, 0x000000);
}

function drawJelly(px: PixelCanvas): void {
  // água-viva
  for (let y = -3; y <= 0; y++) {
    const w = 4 - Math.abs(y);
    px.hLine(-w, y, w * 2 + 1, 0xb0e0ff);
  }
  // tentáculos
  px.vLine(-3, 1, 4, 0xb0e0ff);
  px.vLine(-1, 1, 5, 0xb0e0ff);
  px.vLine(1, 1, 4, 0xb0e0ff);
  px.vLine(3, 1, 5, 0xb0e0ff);
  px.hLine(-3, -3, 4, 0xe8f7ff);
  px.px(-2, -1, 0x294e70);
  px.px(2, -1, 0x294e70);
  px.hLine(-1, 1, 3, 0x77afd2);
}

function drawShellbeast(px: PixelCanvas): void {
  // monstro de concha grande
  for (let y = -4; y <= 4; y++) {
    const w = Math.round(5 * Math.sqrt(Math.max(0, 1 - (y * y) / 25)));
    if (w > 0) px.hLine(-w, y, w * 2 + 1, 0xffe0c8);
  }
  // listras
  px.hLine(-4, -1, 9, 0x8a5a3a);
  px.hLine(-4, 1, 9, 0x8a5a3a);
  // olhos
  px.px(-2, -1, 0x000000);
  px.px(2, -1, 0x000000);
}
